import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { PredictionPosition, PositionsGroupedByPair } from '../../types';
import { UnwindingRule } from '../../types/unwinding.types';
import { useUnwindingSocket } from '../../hooks/useUnwindingSocket';

const fmtPrice = (v: number | undefined | null): string => {
  if (v == null || isNaN(v)) return '—';
  if (v === 0) return '0.000';
  if (v >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

const fmtContracts = (v: number | undefined | null): string => {
  if (v == null || v === 0) return '—';
  return Math.round(v).toString();
};

const findPos = (
  group: PositionsGroupedByPair,
  platform: 'polymarket' | 'kalshi'
): PredictionPosition | undefined =>
  group.positions.find(p => p.platform === platform && p.status === 'open');

const findRule = (
  rules: UnwindingRule[] | undefined,
  pairId: string | null
): UnwindingRule | undefined =>
  pairId ? rules?.find(r => r.pairId === pairId) : undefined;

const hSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.2,
  px: 0.2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.15 } as const;

interface Props {
  groups: PositionsGroupedByPair[];
  rules: UnwindingRule[] | undefined;
  getState: ReturnType<typeof useUnwindingSocket>['getState'];
  onCreateRule: (
    group: PositionsGroupedByPair,
    platform: 'kalshi' | 'polymarket',
    threshold: number
  ) => void;
  onTogglePlatform: (
    rule: UnwindingRule,
    platform: 'kalshi' | 'polymarket',
    enabled: boolean
  ) => void;
  onUpdateThreshold: (rule: UnwindingRule, threshold: number) => Promise<boolean>;
}

const DEFAULT_THRESHOLD = 1.0;

export const UnwindingTabSumOfSides = ({
  groups,
  rules,
  getState,
  onCreateRule,
  onTogglePlatform,
  onUpdateThreshold
}: Props) => {
  if (groups.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
          No open positions found.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...hSx, width: 200, top: 0, backgroundColor: '#1a1a2e' }}>
              Market
            </TableCell>
            <TableCell sx={{ ...hSx, width: 50, top: 0, backgroundColor: '#1a1a2e' }} align="right">
              Pos
            </TableCell>
            <TableCell sx={{ ...hSx, width: 60, top: 0, backgroundColor: '#1a1a2e' }} align="right">
              P.Bid
            </TableCell>
            <TableCell sx={{ ...hSx, width: 60, top: 0, backgroundColor: '#1a1a2e' }} align="right">
              K.Bid
            </TableCell>
            <TableCell sx={{ ...hSx, width: 70, top: 0, backgroundColor: '#1a1a2e' }} align="right">
              Live Sum
            </TableCell>
            <TableCell sx={{ ...hSx, width: 80, top: 0, backgroundColor: '#1a1a2e' }} align="right">
              Threshold
            </TableCell>
            <TableCell sx={{ ...hSx, width: 60, top: 0, backgroundColor: '#1a1a2e' }} align="right">
              Δ
            </TableCell>
            <TableCell sx={{ ...hSx, width: 50, top: 0, backgroundColor: '#1a1a2e' }} align="center">
              P
            </TableCell>
            <TableCell sx={{ ...hSx, width: 50, top: 0, backgroundColor: '#1a1a2e' }} align="center">
              K
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map(group => {
            const rule = findRule(rules, group._id);
            const liveState =
              (group._id ? getState(group._id) : undefined) ??
              (rule ? getState(rule._id) : undefined);
            return (
              <SumRow
                key={group._id ?? group.pairName}
                group={group}
                polyPos={findPos(group, 'polymarket')}
                kalshiPos={findPos(group, 'kalshi')}
                rule={rule}
                liveState={liveState}
                onCreateRule={onCreateRule}
                onTogglePlatform={onTogglePlatform}
                onUpdateThreshold={onUpdateThreshold}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

interface RowProps {
  group: PositionsGroupedByPair;
  polyPos?: PredictionPosition;
  kalshiPos?: PredictionPosition;
  rule?: UnwindingRule;
  liveState?: ReturnType<ReturnType<typeof useUnwindingSocket>['getState']>;
  onCreateRule: (
    group: PositionsGroupedByPair,
    platform: 'kalshi' | 'polymarket',
    threshold: number
  ) => void;
  onTogglePlatform: (
    rule: UnwindingRule,
    platform: 'kalshi' | 'polymarket',
    enabled: boolean
  ) => void;
  onUpdateThreshold: (rule: UnwindingRule, threshold: number) => Promise<boolean>;
}

const SumRow = ({
  group,
  polyPos,
  kalshiPos,
  rule,
  liveState,
  onCreateRule,
  onTogglePlatform,
  onUpdateThreshold
}: RowProps) => {
  const initialThreshold =
    rule?.thresholdSum != null ? String(rule.thresholdSum) : String(DEFAULT_THRESHOLD);
  const [threshold, setThreshold] = useState(initialThreshold);
  const [savedThreshold, setSavedThreshold] = useState(initialThreshold);
  const [saving, setSaving] = useState(false);
  const [togglingPoly, setTogglingPoly] = useState(false);
  const [togglingKalshi, setTogglingKalshi] = useState(false);

  // Sync the input from rule changes (e.g. after another client edited it) only
  // when the user isn't mid-edit.
  useEffect(() => {
    if (rule?.thresholdSum != null && threshold === savedThreshold) {
      const next = String(rule.thresholdSum);
      if (next !== savedThreshold) {
        setThreshold(next);
        setSavedThreshold(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule?.thresholdSum]);

  const isDirty = threshold !== savedThreshold;

  const handleSaveThreshold = useCallback(async () => {
    if (!isDirty || !rule) return;
    const parsed = Number.parseFloat(threshold);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 2) return;
    setSaving(true);
    const ok = await onUpdateThreshold(rule, parsed);
    if (ok) setSavedThreshold(threshold);
    setSaving(false);
  }, [isDirty, threshold, rule, onUpdateThreshold]);

  const doToggle = useCallback(
    (platform: 'kalshi' | 'polymarket', enabled: boolean) => {
      const setLoading = platform === 'kalshi' ? setTogglingKalshi : setTogglingPoly;
      setLoading(true);
      if (rule) {
        onTogglePlatform(rule, platform, enabled);
      } else if (enabled) {
        const parsed = Number.parseFloat(threshold);
        const safeThreshold =
          Number.isFinite(parsed) && parsed > 0 && parsed <= 2 ? parsed : DEFAULT_THRESHOLD;
        onCreateRule(group, platform, safeThreshold);
      }
      setTimeout(() => setLoading(false), 2000);
    },
    [rule, threshold, group, onCreateRule, onTogglePlatform]
  );

  // Live sum: per the trigger semantics, the sum-of-sides rule fires when the
  // hedged-pair revenue (Polymarket sell-side bid + Kalshi sell-side bid for
  // the held outcomes) clears the threshold. We use the live top-of-book bids
  // for the sides the position holds, falling back to position.topBid.
  const polyBid =
    liveState?.polyBid?.price ?? polyPos?.topBid ?? null;
  const kalshiBid =
    liveState?.kalshiBid?.price ?? kalshiPos?.topBid ?? null;
  const liveSum =
    polyBid != null && kalshiBid != null ? polyBid + kalshiBid : null;
  const thresholdNum = Number.parseFloat(threshold);
  const thresholdValid = Number.isFinite(thresholdNum) && thresholdNum >= 0;
  const delta =
    liveSum != null && thresholdValid ? liveSum - thresholdNum : null;
  const meetsThreshold = delta != null && delta >= 0;

  return (
    <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
      {/* Market */}
      <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
        <Tooltip
          title={
            <Box>
              <Typography sx={{ fontSize: '0.7rem' }}>
                {group.polymarketTitle || group.pairName}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
                {group.kalshiTitle || kalshiPos?.marketId || group.pairName}
              </Typography>
            </Box>
          }
          placement="top-start"
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <img
                src="/assets/exchanges/png/100x100/polymarket.png"
                alt="P"
                style={{ width: 13, height: 13, borderRadius: '50%' }}
              />
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {group.polymarketTitle || group.pairName}
              </Typography>
              {polyPos && (
                <Chip
                  label={polyPos.side.toUpperCase()}
                  size="small"
                  color={polyPos.side === 'yes' ? 'success' : 'error'}
                  variant="outlined"
                  sx={{
                    fontSize: '0.42rem',
                    height: 12,
                    '& .MuiChip-label': { px: 0.25 },
                    opacity: 0.7
                  }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <img
                src="/assets/exchanges/png/100x100/kalshi.png"
                alt="K"
                style={{ width: 13, height: 13, borderRadius: '50%' }}
              />
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: 'grey.400',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {group.kalshiTitle || kalshiPos?.marketId || group.pairName}
              </Typography>
              {kalshiPos && (
                <Chip
                  label={kalshiPos.side.toUpperCase()}
                  size="small"
                  color={kalshiPos.side === 'yes' ? 'success' : 'error'}
                  variant="outlined"
                  sx={{
                    fontSize: '0.42rem',
                    height: 12,
                    '& .MuiChip-label': { px: 0.25 },
                    opacity: 0.7
                  }}
                />
              )}
            </Box>
          </Box>
        </Tooltip>
      </TableCell>

      {/* Pos */}
      <TableCell sx={{ ...cSx, verticalAlign: 'top' }} align="right">
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4 }}>
          {fmtContracts(polyPos?.contracts)}
        </Typography>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.4 }}>
          {fmtContracts(kalshiPos?.contracts)}
        </Typography>
      </TableCell>

      {/* P.Bid / K.Bid */}
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
          {fmtPrice(polyBid)}
        </Typography>
      </TableCell>
      <TableCell sx={cSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
          {fmtPrice(kalshiBid)}
        </Typography>
      </TableCell>

      {/* Live Sum */}
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.78rem',
            fontWeight: 700,
            color: meetsThreshold ? '#4caf50' : '#42a5f5'
          }}
        >
          {fmtPrice(liveSum)}
        </Typography>
      </TableCell>

      {/* Threshold (editable) */}
      <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={0} justifyContent="flex-end">
          <TextField
            variant="outlined"
            size="small"
            value={threshold}
            placeholder="1.00"
            onChange={e => setThreshold(e.target.value)}
            onBlur={handleSaveThreshold}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSaveThreshold();
            }}
            disabled={!rule}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.62rem',
                py: 0.15,
                px: 0.2,
                textAlign: 'right',
                width: 38
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.08)'
              },
              minWidth: 0
            }}
            inputProps={{ step: 0.01, min: 0, max: 2 }}
          />
          {saving ? (
            <CircularProgress size={12} sx={{ ml: 0.2 }} />
          ) : isDirty && rule ? (
            <IconButton
              size="small"
              onClick={handleSaveThreshold}
              sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}
            >
              <CheckIcon sx={{ fontSize: '0.8rem' }} />
            </IconButton>
          ) : null}
        </Stack>
      </TableCell>

      {/* Δ to fire */}
      <TableCell sx={cSx} align="right">
        <Typography
          sx={{
            fontSize: '0.7rem',
            color: delta == null ? 'grey.500' : delta >= 0 ? '#4caf50' : '#f44336'
          }}
        >
          {delta == null ? '—' : (delta >= 0 ? '+' : '') + delta.toFixed(3)}
        </Typography>
      </TableCell>

      {/* Maker toggles */}
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingPoly ? (
          <CircularProgress size={14} />
        ) : (
          <Switch
            size="small"
            checked={rule ? rule.polymarketEnabled !== false : false}
            onChange={(_, v) => doToggle('polymarket', v)}
          />
        )}
      </TableCell>
      <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
        {togglingKalshi ? (
          <CircularProgress size={14} />
        ) : (
          <Switch
            size="small"
            checked={rule ? rule.kalshiEnabled !== false : false}
            onChange={(_, v) => doToggle('kalshi', v)}
          />
        )}
      </TableCell>
    </TableRow>
  );
};
